<template>
  <div class="amenity-combos">
    <div class="combo-chips">
      <div
        v-for="combo in amenityCombos"
        :key="combo.id"
        :class="[
          'combo-chip',
          { 'selected': selectedCombos.includes(combo.id) }
        ]"
        @click="toggleCombo(combo.id)"
      >
        <span class="combo-icon">{{ combo.icon }}</span>
        <div class="combo-content">
          <span class="combo-name">{{ combo.name }}</span>
          <span class="combo-description">{{ combo.description }}</span>
        </div>
      </div>
    </div>

    <div v-if="selectedCombos.length > 0" class="selected-combos-info">
      <div class="info-header">{{ $t('Includes these amenities:') }}</div>
      <div class="required-amenities">
        <span
          v-for="amenity in getRequiredAmenities()"
          :key="amenity?.code || 'unknown'"
          class="required-amenity"
        >
          {{ amenity?.name || 'Unknown' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { TOP_AMENITIES } from '~/utils/hotels'

const emit = defineEmits(['update'])

const route = useRoute()
const selectedCombos = ref<string[]>([])

// Define popular amenity combinations
const amenityCombos = [
  {
    id: 'family-friendly',
    name: 'Family-Friendly',
    icon: '👨‍👩‍👧‍👦',
    amenityCodes: [325, 665, 511], // Family Rooms, Pool, Laundry
    description: 'Perfect for families with kids'
  },
  {
    id: 'business-traveler',
    name: 'Business Ready',
    icon: '💼',
    amenityCodes: [989, 366, 11], // Wi-Fi, GYM, 24-Hour Front Desk
    description: 'Essential for business trips'
  },
  {
    id: 'luxury-stay',
    name: 'Luxury Experience',
    icon: '✨',
    amenityCodes: [837, 366, 729, 632], // Spa, GYM, Restaurant, Parking
    description: 'Premium amenities and services'
  },
  {
    id: 'budget-essentials',
    name: 'Budget Essentials',
    icon: '💰',
    amenityCodes: [989, 632, 551], // Wi-Fi, Parking, Luggage Storage
    description: 'Great value with key amenities'
  },
  {
    id: 'pet-friendly',
    name: 'Pet-Friendly',
    icon: '🐕',
    amenityCodes: [1009, 372, 632], // Pets Allowed, Garden, Parking
    description: 'Welcome your furry friends'
  },
  {
    id: 'accessible-stay',
    name: 'Accessible',
    icon: '♿',
    amenityCodes: [971, 11, 41], // Wheelchair-Accessible, 24-Hour Front Desk, Airport Shuttle
    description: 'Accessible facilities and services'
  },
  {
    id: 'wellness-focused',
    name: 'Wellness & Fitness',
    icon: '🏃‍♀️',
    amenityCodes: [837, 366, 617], // Spa, Fitness Center, Outdoor Pool
    description: 'Health and wellness amenities'
  },
  {
    id: 'extended-stay',
    name: 'Extended Stay',
    icon: '🏠',
    amenityCodes: [494, 511, 989], // Kitchen, Laundry, Wi-Fi
    description: 'Comfort for longer stays'
  }
]

// Get amenity details by code
const getAmenityByCode = (code: number) => {
  return TOP_AMENITIES.find(amenity => amenity.code === code)
}

// Get all required amenities for selected combos
const getRequiredAmenities = () => {
  const requiredCodes = new Set<number>()
  
  selectedCombos.value.forEach(comboId => {
    const combo = amenityCombos.find(c => c.id === comboId)
    if (combo) {
      combo.amenityCodes.forEach(code => requiredCodes.add(code))
    }
  })
  
  return Array.from(requiredCodes)
    .map(code => getAmenityByCode(code))
    .filter(Boolean)
}

// Initialize from route query
onMounted(() => {
  const combosQuery = route.query.amenity_combos as string
  if (combosQuery) {
    selectedCombos.value = combosQuery.split(',').filter(Boolean)
  }
})

// Watch route changes
watch(() => route.query.amenity_combos, (newCombos) => {
  const combos = typeof newCombos === 'string' ? newCombos.split(',').filter(Boolean) : []
  selectedCombos.value = combos
})

const toggleCombo = (comboId: string) => {
  const index = selectedCombos.value.indexOf(comboId)
  if (index > -1) {
    selectedCombos.value.splice(index, 1)
  } else {
    selectedCombos.value.push(comboId)
  }
  emitUpdate()
}

const emitUpdate = () => {
  // Convert selected combos to amenity codes
  const allRequiredCodes = new Set<number>()
  
  selectedCombos.value.forEach(comboId => {
    const combo = amenityCombos.find(c => c.id === comboId)
    if (combo) {
      combo.amenityCodes.forEach(code => allRequiredCodes.add(code))
    }
  })
  
  emit('update', {
    selectedCombos: selectedCombos.value,
    requiredAmenityCodes: Array.from(allRequiredCodes)
  })
}
</script>

<style scoped>
.amenity-combos {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.combo-chips {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.combo-chip {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--white);
  user-select: none;
}

.combo-chip:hover {
  border-color: var(--blue);
  background-color: rgba(25, 133, 249, 0.04);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.combo-chip.selected {
  background: linear-gradient(135deg, var(--blue) 0%, #1565c0 100%);
  border-color: var(--blue);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(25, 133, 249, 0.35);
}

.combo-chip.selected .combo-description {
  color: rgba(255, 255, 255, 0.9);
}

.combo-icon {
  font-size: 32px;
  line-height: 1;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.combo-chip.selected .combo-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.combo-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.combo-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: #1a1a1a;
}

.combo-chip.selected .combo-name {
  color: white;
}

.combo-description {
  font-size: 12px;
  line-height: 1.4;
  color: var(--dark-grey);
  font-weight: 400;
}

.selected-combos-info {
  background: linear-gradient(135deg, #f8f9fa 0%, #e8f4fd 100%);
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e3f2fd;
}

.info-header {
  font-size: 13px;
  font-weight: 600;
  color: #1565c0;
  margin-bottom: 10px;
}

.required-amenities {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.required-amenity {
  font-size: 12px;
  background-color: white;
  color: #1565c0;
  padding: 6px 10px;
  border-radius: 16px;
  border: 1px solid #bbdefb;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
</style>